import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Card, 
  Row, 
  Col, 
  Descriptions, 
  Tag, 
  Button, 
  Spin, 
  message, 
  Divider,
  Space,
  Typography
} from 'antd';
import { 
  ArrowLeftOutlined, 
  EditOutlined, 
  CalendarOutlined,
  UserOutlined,
  DollarOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { fetchOfflineBookings, fetchPaymentForOfflineBooking } from './OfflineBookingActions';
import '../../../assets/scss/components/OfflineBooking/ViewOfflineBooking.scss';

const { Title, Text } = Typography;

const ViewOfflineBooking = () => {
  const history = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();
  
  const [booking, setBooking] = useState(null);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(true);
  
  // Get bookings from Redux state - this will update when fetch completes
  const offlineBookingState = useSelector(state => state.offlineBooking || {});
  const bookings = offlineBookingState.bookings || [];

  useEffect(() => {
    loadBookingData();
  }, [id]);

  const loadBookingData = async () => {
    try {
      setLoading(true);
      
      // Always fetch fresh data from server
      console.log('Fetching offline bookings from server...');
      await dispatch(fetchOfflineBookings());
      
      // Wait a bit for Redux state to update, then check for the booking
      await new Promise(resolve => setTimeout(resolve, 300));
      
    } catch (error) {
      console.error('Error loading booking:', error);
      message.error('Failed to load booking details');
      setLoading(false);
      setTimeout(() => {
        history.push('/dashboard/offline-booking-list');
      }, 1500);
    }
  };

  // Separate effect to check for booking after state updates
  useEffect(() => {
    if (!loading && bookings.length > 0 && !booking) {
      // Find the booking by ID
      const foundBooking = bookings.find(b => {
        const bookingId = b.id || b._id;
        return bookingId === id;
      });
      
      if (foundBooking) {
        console.log('Booking found:', foundBooking);
        setBooking(foundBooking);
        
        // Fetch payment details
        const fetchPayment = async () => {
          setPaymentLoading(true);
          try {
            const paymentResponse = await dispatch(fetchPaymentForOfflineBooking(id));
            if (paymentResponse && paymentResponse.payload) {
              setPayment(paymentResponse.payload);
            }
          } catch (paymentError) {
            console.log('No payment found for this booking:', paymentError);
          }
          setPaymentLoading(false);
        };
        fetchPayment();
      } else if (offlineBookingState.loading === false) {
        // Only show error if loading is complete and still not found
        console.error('Booking not found with ID:', id);
        message.error('Booking not found');
        setTimeout(() => {
          history.push('/dashboard/offline-booking-list');
        }, 1500);
      }
    }
  }, [bookings, loading, booking, id, offlineBookingState.loading]);

  // Check when bookings are loaded
  useEffect(() => {
    if (offlineBookingState.loading === false && bookings.length > 0) {
      setLoading(false);
    }
  }, [offlineBookingState.loading, bookings]);

  const handleEdit = () => {
    if (booking) {
      history.push('/dashboard/booking/offline', {
        editRecord: booking,
        editMode: true
      });
    }
  };

  const handleBack = () => {
    history.push('/dashboard/offline-booking-list');
  };

  const getStatusTag = (status) => {
    const colors = {
      INITIATED: 'blue',
      TENTATIVE: 'orange',
      CONFIRMED: 'green',
      COMPLETED: 'success',
      CANCELLED: 'red',
      PENDING: 'orange',
      DECLINED: 'red',
      TERMINATED: 'default',
      WITHDRAWN: 'default'
    };
    return <Tag color={colors[status] || 'default'}>{status || 'INITIATED'}</Tag>;
  };

  const getPaymentStatusTag = (status) => {
    if (!status) return <Tag color="default">No Payment</Tag>;
    
    const statusConfig = {
      INITIATED: { color: 'warning', text: 'Pending' },
      PENDING: { color: 'warning', text: 'Pending' },
      COMPLETED: { color: 'success', text: 'Paid' },
      SUCCESS: { color: 'success', text: 'Paid' },
      CANCELLED: { color: 'error', text: 'Cancelled' },
      FAILED: { color: 'error', text: 'Failed' }
    };
    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const formatCurrency = (amount, currency = 'LKR') => {
    if (!amount && amount !== 0) return '-';
    return `${currency} ${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip="Loading booking details..." />
      </div>
    );
  }

  if (!booking) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Text>Booking not found</Text>
        <br />
        <Button type="primary" onClick={handleBack} style={{ marginTop: 16 }}>
          Back to List
        </Button>
      </div>
    );
  }

  const clientName = booking.user?.name || booking.clientName || 'N/A';
  const clientEmail = booking.user?.email || booking.clientEmail || 'N/A';
  const clientPhone = booking.user?.contactNumber || booking.clientPhone || 'N/A';
  const spaceName = booking.title || booking.spaceName || booking.space?.spaceName || booking.space?.name || 'N/A';

  // Check if payment is completed
  const paymentStatus = payment?.paymentStatus || payment?.status;
  const isPaymentCompleted = paymentStatus === 'COMPLETED' || paymentStatus === 'SUCCESS';
  const isCancelled = booking.bookingStatus === 'CANCELLED';
  const isWithdrawn = booking.bookingStatus === 'WITHDRAWN';

  return (
    <div className="view-offline-booking-container">
      {/* Header */}
      <div className="view-booking-header">
        <div className="header-left">
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBack}
            style={{ marginRight: 16 }}
          >
            Back to List
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            Booking Details - {booking.bookingRef || booking.id?.substring(0, 8)}
          </Title>
        </div>
        <div className="header-right">
          <Space>
            {getStatusTag(booking.bookingStatus)}
            {!isCancelled && !isPaymentCompleted && !isWithdrawn && (
              <Button 
                type="primary" 
                icon={<EditOutlined />}
                onClick={handleEdit}
              >
                Edit Booking
              </Button>
            )}
          </Space>
        </div>
      </div>

      <Row gutter={[24, 24]}>
        {/* Booking Information */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <CalendarOutlined />
                <span>Booking Information</span>
              </Space>
            }
            className="info-card"
          >
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Booking Reference">
                <Text strong>{booking.bookingRef || booking.id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Space">
                {spaceName}
              </Descriptions.Item>
              <Descriptions.Item label="Event Type">
                {booking.eventType || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Charging Type">
                {booking.chargingType || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Start Date">
                {moment(booking.bookingStartDate).format('DD MMM YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="End Date">
                {moment(booking.bookingEndDate).format('DD MMM YYYY')}
              </Descriptions.Item>
              {booking.pricingOptions && booking.pricingOptions.length > 0 && (
                <Descriptions.Item label="Time Slot">
                  {booking.pricingOptions[0].startTime} - {booking.pricingOptions[0].endTime}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Duration">
                {booking.duration || 1} {booking.chargingType === 'Hourly' ? 'hour(s)' : 
                  booking.chargingType === 'Daily' ? 'day(s)' : 
                  booking.chargingType === 'Weekly' ? 'week(s)' : 
                  booking.chargingType === 'Monthly' ? 'month(s)' : 'unit(s)'}
              </Descriptions.Item>
              <Descriptions.Item label="Guest Count">
                {booking.guest || booking.numberOfGuests || 1}
              </Descriptions.Item>
              {booking.seatArrangement && (
                <Descriptions.Item label="Seating Arrangement">
                  {booking.seatArrangement.name}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Booking Status">
                {getStatusTag(booking.bookingStatus)}
              </Descriptions.Item>
              <Descriptions.Item label="Created On">
                {booking.createdDate ? moment(booking.createdDate).format('DD MMM YYYY, h:mm A') : 'N/A'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Client Information */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <UserOutlined />
                <span>Client Information</span>
              </Space>
            }
            className="info-card"
          >
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Client Name">
                <Text strong>{clientName}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {clientEmail}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {clientPhone}
              </Descriptions.Item>
              {booking.user?.id && (
                <Descriptions.Item label="User ID">
                  {booking.user.id}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>

        {/* Financial Details */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <DollarOutlined />
                <span>Financial Details</span>
              </Space>
            }
            className="info-card"
          >
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Base Booking Value">
                {formatCurrency(booking.financialDetails?.baseBookingValue || booking.totalPrice, booking.currency)}
              </Descriptions.Item>
              {booking.financialDetails?.hostDiscountPercent > 0 && (
                <>
                  <Descriptions.Item label="Host Discount">
                    {booking.financialDetails.hostDiscountPercent}% 
                    ({formatCurrency(booking.financialDetails.hostDiscountValue, booking.currency)})
                  </Descriptions.Item>
                  <Descriptions.Item label="After Host Discount">
                    {formatCurrency(booking.financialDetails.bookingAfterHostDiscount, booking.currency)}
                  </Descriptions.Item>
                </>
              )}
              {booking.financialDetails?.msDiscountPercent > 0 && (
                <>
                  <Descriptions.Item label="MS Discount">
                    {booking.financialDetails.msDiscountPercent}% 
                    ({formatCurrency(booking.financialDetails.msDiscountValue, booking.currency)})
                  </Descriptions.Item>
                  <Descriptions.Item label="After All Discounts">
                    {formatCurrency(booking.financialDetails.bookingAfterAllDiscounts, booking.currency)}
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="Tax Amount">
                {formatCurrency(booking.financialDetails?.taxAmount, booking.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Total Amount">
                <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                  {formatCurrency(booking.financialDetails?.finalBookingValue || booking.totalPrice, booking.currency)}
                </Text>
              </Descriptions.Item>
              {/* Price Adjustment Information */}
              {booking.financialDetails?.isPriceAdjusted && (
                <>
                  <Divider style={{ margin: '12px 0' }}>Price Adjustment</Divider>
                  <Descriptions.Item label="Original Price">
                    {formatCurrency(booking.financialDetails.originalCalculatedPrice, booking.currency)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Adjustment">
                    <Text style={{ 
                      color: booking.financialDetails.priceAdjustmentAmount >= 0 ? '#52c41a' : '#ff4d4f',
                      fontWeight: 500
                    }}>
                      {booking.financialDetails.priceAdjustmentAmount >= 0 ? '+' : ''}
                      {formatCurrency(booking.financialDetails.priceAdjustmentAmount, booking.currency)}
                      {' '}
                      ({booking.financialDetails.priceAdjustmentPercent >= 0 ? '+' : ''}
                      {booking.financialDetails.priceAdjustmentPercent?.toFixed(2)}%)
                    </Text>
                  </Descriptions.Item>
                  {booking.financialDetails.priceAdjustmentReason && (
                    <Descriptions.Item label="Adjustment Reason">
                      <Text italic>{booking.financialDetails.priceAdjustmentReason}</Text>
                    </Descriptions.Item>
                  )}
                  {booking.financialDetails.adjustedBy && (
                    <Descriptions.Item label="Adjusted By">
                      {booking.financialDetails.adjustedBy}
                    </Descriptions.Item>
                  )}
                  {booking.financialDetails.adjustmentDate && (
                    <Descriptions.Item label="Adjusted On">
                      {moment(booking.financialDetails.adjustmentDate).format('DD MMM YYYY, h:mm A')}
                    </Descriptions.Item>
                  )}
                </>
              )}
              <Divider style={{ margin: '12px 0' }} />
              <Descriptions.Item label="Commission">
                {booking.financialDetails?.commissionPercent || 0}% 
                ({formatCurrency(booking.financialDetails?.baseCommission, booking.currency)})
              </Descriptions.Item>
              {booking.financialDetails?.ccChargePercent > 0 && (
                <Descriptions.Item label="CC Charges">
                  {booking.financialDetails.ccChargePercent}% 
                  ({formatCurrency(booking.financialDetails.ccChargeValue, booking.currency)})
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Commission to MS">
                {formatCurrency(booking.financialDetails?.finalCommissionToMS, booking.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Payment to Host">
                <Text strong style={{ color: '#52c41a' }}>
                  {formatCurrency(booking.financialDetails?.paymentToHost, booking.currency)}
                </Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Payment Details */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <FileTextOutlined />
                <span>Payment Details</span>
              </Space>
            }
            className="info-card"
            loading={paymentLoading}
          >
            {payment ? (
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Payment ID">
                  {payment.id?.substring(0, 12) || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Payment Status">
                  {getPaymentStatusTag(payment.paymentStatus || payment.status)}
                </Descriptions.Item>
                <Descriptions.Item label="Payment Amount">
                  <Text strong>
                    {formatCurrency(payment.amount, payment.currency || booking.currency)}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Payment Method">
                  {payment.paymentMethod || payment.paymentType || 'N/A'}
                </Descriptions.Item>
                {payment.transactionReference && (
                  <Descriptions.Item label="Transaction Reference">
                    {payment.transactionReference}
                  </Descriptions.Item>
                )}
                {payment.paymentScheduleOn && (
                  <Descriptions.Item label="Payment Scheduled On">
                    {moment(payment.paymentScheduleOn).format('DD MMM YYYY')}
                  </Descriptions.Item>
                )}
                {payment.paymentDescription && (
                  <Descriptions.Item label="Description">
                    {payment.paymentDescription}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Payment Created">
                  {payment.createdDate ? moment(payment.createdDate).format('DD MMM YYYY, h:mm A') : 'N/A'}
                </Descriptions.Item>
                {payment.lastModifiedDate && (
                  <Descriptions.Item label="Last Updated">
                    {moment(payment.lastModifiedDate).format('DD MMM YYYY, h:mm A')}
                  </Descriptions.Item>
                )}
              </Descriptions>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                <FileTextOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                <p>No payment record found for this booking</p>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ViewOfflineBooking;
